import { useQuery } from '@tanstack/react-query';
import {
  ArrowDownRight,
  ArrowUpRight,
  Banknote,
  CalendarDays,
  CreditCard,
  DollarSign,
  LineChart,
  PackageOpen,
  Receipt,
  ShoppingBasket,
  Smartphone,
  TrendingUp,
  UsersRound,
  Utensils,
  Wallet,
} from 'lucide-react';
import type { ReactNode } from 'react';

import { Spinner } from '@/components/ui/Spinner';
import OnboardingChecklist from '@/components/OnboardingChecklist';
import { getDashboard, type DashboardPayload } from '@/services/admin';
import { formatRub, paymentMethodLabels } from '@/utils/format';

export default function Dashboard() {
  const { data, isLoading } = useQuery({ queryKey: ['admin/dashboard'], queryFn: getDashboard });

  // «Выручка/средний чек = полученные деньги» (received). Старый бэк это поле не
  // отдаёт — мягкий fallback на today, чтобы дашборд не падал до обновления API.
  const receivedTotal = data ? data.received?.total ?? data.today.revenue : 0;
  const receivedAvg = data ? data.received?.avgCheck ?? data.today.avgCheck : 0;

  return (
    <div>
      <header className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-semibold">Главная</h1>
          <p className="text-sm text-slate-500">Обзор ключевых показателей</p>
        </div>
      </header>

      <OnboardingChecklist />

      {isLoading && <Spinner label="Загружаем данные" />}

      {data && (
        <>
          {/* ── Ряд 1: Заказы сегодня / Выручка сегодня / Средний чек / Активные блюда ── */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            <MetricCard
              title="Заказы сегодня"
              value={data.today.count.toLocaleString('ru-RU')}
              hint={`За неделю: ${data.week.count.toLocaleString('ru-RU')}`}
              delta={data.deltas.ordersCount}
              deltaLabel="от вчера"
              icon={<ShoppingBasket size={22} />}
              tone="brand"
            />
            <MetricCard
              title="Выручка сегодня"
              value={formatRub(receivedTotal)}
              hint={`За неделю: ${formatRub(data.week.revenue)}`}
              delta={data.deltas.revenue}
              deltaLabel="от вчера"
              icon={<DollarSign size={22} />}
              tone="success"
            />
            <MetricCard
              title="Средний чек"
              value={formatRub(receivedAvg)}
              delta={data.deltas.avgCheck}
              deltaLabel="от вчера"
              icon={<LineChart size={22} />}
              tone="warning"
            />
            <MetricCard
              title="Активные блюда"
              value={data.activeDishes.toLocaleString('ru-RU')}
              hint="В меню"
              icon={<Utensils size={22} />}
              tone="violet"
            />
          </div>

          {/* ── Ряд 2: Наличные и Карта ── */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <PaymentCard
              title="Наличными"
              methods={data.paymentMethods}
              keys={['CASH']}
              totalToday={receivedTotal}
              icon={<Banknote size={22} />}
              tone="success"
            />
            <PaymentCard
              title="Картой"
              methods={data.paymentMethods}
              keys={['CARD', 'QRCODE', 'TRANSFER', 'ONLINE', 'MIXED']}
              totalToday={receivedTotal}
              icon={<CreditCard size={22} />}
              tone="brand"
            />
          </div>

          {/* ── Ряд 3: Заказы / Выручка за месяц / Всего клиентов ── */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <MetricCard
              title="Заказы за месяц"
              value={data.month.count.toLocaleString('ru-RU')}
              icon={<CalendarDays size={22} />}
              tone="brand"
            />
            <MetricCard
              title="Выручка за месяц"
              value={formatRub(data.month.revenue)}
              icon={<DollarSign size={22} />}
              tone="success"
            />
            <MetricCard
              title="Всего клиентов"
              value={data.totalClients.toLocaleString('ru-RU')}
              icon={<UsersRound size={22} />}
              tone="violet"
            />
          </div>

          {data.profit && <ProfitRow profit={data.profit} />}

          {/* ── Дополнительные блоки ── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
            <div className="card p-5">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Banknote size={18} /> Оплата по способам (сегодня)
              </h3>
              <div className="space-y-2">
                {data.paymentMethods.filter((p) => p.amount > 0).length === 0 ? (
                  <div className="text-sm text-slate-400">Нет данных.</div>
                ) : (
                  data.paymentMethods
                    .filter((p) => p.amount > 0)
                    .map((p) => (
                      <div key={p.method} className="flex justify-between items-center text-sm">
                        <span>{paymentMethodLabels[p.method] ?? p.method}</span>
                        <span className="flex gap-4">
                          <span className="text-slate-400">{p.count} шт</span>
                          <span className="font-medium">{formatRub(p.amount)}</span>
                        </span>
                      </div>
                    ))
                )}
              </div>
            </div>

            <div className="card p-5">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Smartphone size={18} /> Заказы из приложения (сегодня)
              </h3>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-3xl font-semibold">{data.appOrders.count}</div>
                  <div className="text-sm text-slate-500 mt-1">
                    Выручка: {formatRub(data.appOrders.revenue)}
                  </div>
                </div>
                <div className="p-3 rounded-lg bg-brand-50 text-brand-600">
                  <Smartphone size={26} />
                </div>
              </div>
            </div>
          </div>

          <div className="card p-5 mt-6">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <TrendingUp size={18} /> Топ-5 блюд за сегодня
            </h3>
            {data.topDishes.length === 0 ? (
              <div className="text-sm text-slate-400">Продажи ещё не начались.</div>
            ) : (
              <div className="divide-y divide-slate-100">
                {data.topDishes.map((d, idx) => (
                  <div key={d.id} className="flex items-center justify-between py-2.5 text-sm">
                    <span className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full bg-brand-50 text-brand-600 text-xs flex items-center justify-center font-semibold">
                        {idx + 1}
                      </span>
                      <span>{d.name}</span>
                    </span>
                    <span className="flex gap-4 items-baseline">
                      <span className="text-slate-400">{d.quantity} шт</span>
                      {(d.cost ?? 0) > 0 && (
                        <span className="text-xs text-emerald-600">
                          прибыль {formatRub(d.profit ?? 0)}
                        </span>
                      )}
                      <span className="font-medium">{formatRub(d.total)}</span>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// Прибыль за сегодня: закупка, валовая и чистая. Плюс месяц одной строкой —
// по дню трудно понять тренд, а лезть в аналитику ради одной цифры не хочется.
function ProfitRow({ profit }: { profit: NonNullable<DashboardPayload['profit']> }) {
  const t = profit.today;
  const m = profit.month;
  return (
    <div className="mt-6">
      <h3 className="font-semibold mb-3 flex items-center gap-2">
        <Wallet size={18} /> Прибыль сегодня
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <MetricCard
          title="Себестоимость"
          value={formatRub(t.cogs)}
          hint={t.cogs === 0 ? 'Заполните закупочные цены блюд' : `Фудкост ${t.foodCostPercent}%`}
          icon={<PackageOpen size={22} />}
          tone="warning"
        />
        <MetricCard
          title="Валовая прибыль"
          value={formatRub(t.grossProfit)}
          hint={`Маржа ${t.marginPercent}% · за месяц ${formatRub(m.grossProfit)}`}
          icon={<TrendingUp size={22} />}
          tone={t.grossProfit < 0 ? 'rose' : 'success'}
        />
        <MetricCard
          title="Расходы"
          value={formatRub(t.expenses)}
          hint={`За месяц: ${formatRub(m.expenses)}`}
          icon={<Receipt size={22} />}
          tone="rose"
        />
        <MetricCard
          title="Чистая прибыль"
          value={formatRub(t.netProfit)}
          hint={`За месяц: ${formatRub(m.netProfit)}`}
          icon={<Wallet size={22} />}
          tone={t.netProfit < 0 ? 'rose' : 'success'}
        />
      </div>
      {t.costCoverage < 95 && (
        <div className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          Себестоимость известна у {t.costCoverage}% сегодняшних продаж — маржа считается
          только по ним. Закупочные цены задаются в карточке блюда.
        </div>
      )}
    </div>
  );
}

// ── UI helpers ──────────────────────────────────────────────

type Tone = 'brand' | 'success' | 'warning' | 'violet' | 'rose' | 'slate';

const toneBg: Record<Tone, string> = {
  brand: 'from-brand-50/70 to-transparent',
  success: 'from-emerald-50/80 to-transparent',
  warning: 'from-amber-50/80 to-transparent',
  violet: 'from-violet-50/80 to-transparent',
  rose: 'from-rose-50/80 to-transparent',
  slate: 'from-slate-50/80 to-transparent',
};
const toneBadge: Record<Tone, string> = {
  brand: 'bg-brand-500 text-white',
  success: 'bg-emerald-500 text-white',
  warning: 'bg-amber-400 text-white',
  violet: 'bg-violet-500 text-white',
  rose: 'bg-rose-500 text-white',
  slate: 'bg-slate-500 text-white',
};
const toneTitle: Record<Tone, string> = {
  brand: 'text-brand-600',
  success: 'text-emerald-600',
  warning: 'text-amber-600',
  violet: 'text-violet-600',
  rose: 'text-rose-600',
  slate: 'text-slate-600',
};

function MetricCard({
  title,
  value,
  hint,
  delta,
  deltaLabel,
  icon,
  tone,
}: {
  title: string;
  value: string;
  hint?: string;
  delta?: number;
  deltaLabel?: string;
  icon: ReactNode;
  tone: Tone;
}) {
  const isUp = delta != null && delta > 0;
  const isDown = delta != null && delta < 0;
  const deltaColor = isUp ? 'text-emerald-600' : isDown ? 'text-rose-600' : 'text-slate-400';
  return (
    <div className={`card p-5 bg-gradient-to-br ${toneBg[tone]} relative overflow-hidden`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className={`text-xs font-medium ${toneTitle[tone]}`}>{title}</div>
          <div className="text-2xl font-semibold mt-2">{value}</div>
          {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
          {delta != null && deltaLabel && (
            <div className={`mt-1 text-xs flex items-center gap-1 font-medium ${deltaColor}`}>
              {isUp && <ArrowUpRight size={12} />}
              {isDown && <ArrowDownRight size={12} />}
              <span>
                {delta > 0 ? '+' : ''}
                {delta}% {deltaLabel}
              </span>
            </div>
          )}
        </div>
        <div className={`shrink-0 h-10 w-10 rounded-lg flex items-center justify-center ${toneBadge[tone]}`}>
          {icon}
        </div>
      </div>
    </div>
  );
}

function PaymentCard({
  title,
  methods,
  keys,
  totalToday,
  icon,
  tone,
}: {
  title: string;
  methods: DashboardPayload['paymentMethods'];
  keys: string[];
  totalToday: number;
  icon: ReactNode;
  tone: Tone;
}) {
  const filtered = methods.filter((m) => keys.includes(m.method));
  const amount = filtered.reduce((s, m) => s + m.amount, 0);
  const count = filtered.reduce((s, m) => s + m.count, 0);
  const share = totalToday > 0 ? Math.round((amount / totalToday) * 1000) / 10 : 0;

  return (
    <div className={`card p-5 bg-gradient-to-br ${toneBg[tone]}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className={`text-xs font-medium ${toneTitle[tone]}`}>{title}</div>
          <div className="text-2xl font-semibold mt-2">{formatRub(amount)}</div>
          <div className="text-xs text-slate-500 mt-1">
            {count} {paymentWord(count)}{share > 0 ? ` (${share.toLocaleString('ru-RU')}%)` : ''}
          </div>
        </div>
        <div className={`shrink-0 h-10 w-10 rounded-lg flex items-center justify-center ${toneBadge[tone]}`}>
          {icon}
        </div>
      </div>
    </div>
  );
}

// Карточки показывают число ПЛАТЕЖЕЙ (строк оплат), а не заказов: смешанная
// оплата = несколько платежей на один заказ, поэтому «заказами» это называть нельзя.
function paymentWord(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'платёж';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return 'платежа';
  return 'платежей';
}
